import { ResourceFillerService } from './identity/resource-filler.service';
import { ApplicationStarterService } from './application-starter.service';
import { IdentityParserService } from './identity/identity-parser.service';
import { IdentityProviderService } from './identity/identity-provider.service';
import { CurrentUserService } from './identity/current-user.service';
import { CurrentSiteIdStore } from './current-site-id.store';
import { LoaderService } from './loader.service';
import { UserSession } from './user-session';
import { TriggerbeeRequesterMock } from './triggerbee/triggerbee-requester.mock';
import { APP_INITIALIZER, NgModule, Optional, SkipSelf } from '@angular/core';
import { HttpClient, provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import { SiteContextService } from './site-context.service';
/*  This module includes providers for the singleton services that loads when the application starts.
    It's a pure services module and should not have any declarations.
    The module should only be imported by the root AppModule only once. */

import { TitleService } from './title.service';
import { LanguageService } from './language.service';
import { VerifiedGuard } from './authorization/verified-user.guard';
import { AdminUserGuard } from './authorization/admin-user.guard';
import { SiteAdminUserGuard } from './authorization/site-admin-user.guard';
import { AnalyticsGuard } from './authorization/analytics.guard';

import { IdentityService } from './identity/identity.service';
import { CookieService } from './cookie.service';
import { ENVIRONMENT_DI, ENVIRONMENT } from './../environment/environment';

import { LoggerFactory } from './logger.service';
import { SnackBarService } from './snack-bar.service';
import { TriggerbeeRequester } from './triggerbee/triggerbee-requester.service';
import { TriggerbeeUrlBuilder } from './triggerbee/triggerbee-url-builder.service';

import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { GravatarService } from 'app/core/gravatar.service';
import { DemoAccountsService } from '../shared/services/demo-accounts.service';
import { MtrService } from './tracker/mtr.service';
import { AmplitudeService, IAmplitude } from './tracker/amplitude.service';
import { IMtr } from './tracker/mtr.interface';
import { ExpiredAccountGuard } from './authorization/expired-account.guard';
import { ExpiredAccountAccessService } from './authorization/expired-account-access.service';
import { HttpRequestDecorator } from './triggerbee/http.request.decorator';
import { PruningTranslationLoader } from './translate-pruning-loader/pruning-loader';
import { TierAccountGuard } from './authorization/tier-account.guard';
import { StaleChunkReloadService } from './stale-chunk-reload.service';

@NgModule({
  imports: [
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
  ],
  providers: [
    /* -- Guards -- */
    AnalyticsGuard,
    VerifiedGuard,
    AdminUserGuard,
    SiteAdminUserGuard,
    ExpiredAccountGuard,
    TierAccountGuard,
    /* -- Services -- */
    LoggerFactory,
    LoaderService,
    LanguageService,
    SnackBarService,
    UserSession,
    ExpiredAccountAccessService,
    CookieService,
    TriggerbeeUrlBuilder,
    ApplicationStarterService,
    StaleChunkReloadService,
    /* Identity */
    IdentityParserService,
    IdentityProviderService,
    CurrentUserService,
    CurrentSiteIdStore,
    ResourceFillerService,
    IdentityService,
    HttpRequestDecorator,
    {
      provide: APP_INITIALIZER,
      useFactory: (identityProvider: IdentityProviderService) => () => identityProvider.loadInitialIdentityAsync(),
      deps: [IdentityProviderService],
      multi: true,
    },
    /* -------- */
    TitleService,
    SiteContextService,
    {
      provide: 'Mtr',
      useFactory: (loggerFactory: LoggerFactory) => provideMtr(loggerFactory),
      deps: [LoggerFactory],
    },
    {
      provide: 'Amplitude',
      useFactory: (loggerFactory: LoggerFactory) => provideAmplitude(loggerFactory),
      deps: [LoggerFactory],
    },
    DemoAccountsService,
    TriggerbeeRequesterMock,
    TriggerbeeRequester,
    {
      provide: ENVIRONMENT_DI,
      useValue: ENVIRONMENT,
    },
    {
      provide: TriggerbeeRequester,
      useFactory: (
        http: HttpClient,
        loggerFactory: LoggerFactory,
        urlBuilder: TriggerbeeUrlBuilder,
        loader: LoaderService,
      ) => provideRequester(http, loggerFactory, urlBuilder, loader),
      deps: [HttpClient, LoggerFactory, TriggerbeeUrlBuilder, LoaderService],
    },
    GravatarService,
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
  ],
})
export class CoreModule {
  constructor(@Optional() @SkipSelf() parentModule: CoreModule) {
    this.throwIfAlreadyLoaded(parentModule, 'CoreModule');
  }
  throwIfAlreadyLoaded(parentModule: any, moduleName: string): void {
    if (parentModule) {
      throw new Error(`${moduleName} has already been loaded. Import Core modules in the AppModule only.`);
    }
  }
}

export function HttpLoaderFactory(http: HttpClient) {
  return new PruningTranslationLoader(http);
}

function provideRequester(
  http: HttpClient,
  loggerFactory: LoggerFactory,
  urlBuilder: TriggerbeeUrlBuilder,
  loader: LoaderService,
) {
  return new TriggerbeeRequester(http, loggerFactory, urlBuilder, loader);
}

function provideMtr(loggerFactory: LoggerFactory): IMtr {
  return new MtrService(loggerFactory);
}

function provideAmplitude(loggerFactory: LoggerFactory): IAmplitude {
  return new AmplitudeService(ENVIRONMENT, loggerFactory);
}
